import {ChangeDetectionStrategy, Component, effect, Injectable, input, output, Signal} from "@angular/core";
import {EnkelGruppeModell, GruppeId, Gruppetype, OppdaterGruppeSpesifikasjon, Språk} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {takeUntilDestroyed, toObservable, toSignal} from "@angular/core/rxjs-interop";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {InlineSkjemaHandlingerKomponent} from "web-ng2/delt/komponenter/inline-skjema/inline-skjema-handlinger/inline-skjema-handlinger";
import {InlineSkjemaKomponent} from "web-ng2/delt/komponenter/inline-skjema/inline-skjema";
import {InlineSkjemaTittelKomponent} from "web-ng2/delt/komponenter/inline-skjema/inline-skjema-tittel/inline-skjema-tittel";
import {MatButton} from "@angular/material/button";
import {MatFormFieldModule} from "@angular/material/form-field";
import {NavigasjonforhindretFlashDirektiv} from "web-ng2/delt/direktiver/navigasjonforhindret-flash/navigasjonforhindret-flash";
import {TranslateModule} from "@ngx-translate/core";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {StandardValideringsfeilPipe} from "felles-ng2/pipes/StandardValideringsfeilPipe";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {Appkonfigurasjon} from "felles-ng2/tjenester/Appkonfigurasjon";
import {InlineMeldingKomponent} from "web-ng2/delt/komponenter/inline-melding/inline-melding";
import {MatSlideToggleModule} from "@angular/material/slide-toggle";
import {GruppeDatatjeneste, GruppeModellDML} from "felles-ng2/tjenester/datalager/GruppeDatatjeneste";
import {aktiverFormKontroller} from "felles-ng2/infrastruktur/angular/reactiveForm";
import {LokaleVelgerKomponent} from "web-ng2/delt/komponenter/skjema/lokale-velger/lokale-velger";
import {TidssoneVelgerKomponent} from "web-ng2/delt/komponenter/skjema/tidssone-velger/tidssone-velger";
import {MatInput} from "@angular/material/input";
import {Observable, switchMap} from "rxjs";
import {DataOgMeta, mapTilData, terminerEtterFørsteRequestRespons} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {logMottatt} from "felles-ng2/tjenester/datalager/datatjeneste";
import {KreverTilgangsrolleBrukerDirektiv} from "web-ng2/delt/direktiver/kreverTilgangsrolleBruker/kreverTilgangsrolleBruker";
import {GruppeLogoOgFargeSkjemaKomponent} from "web-ng2/moduler/gruppe/komponenter/gruppe-logo-og-farge-skjema/gruppe-logo-og-farge-skjema";
import {BrukerkontekstAdministrasjon} from "felles-ng2/tjenester/BrukerkontekstAdministrasjon";
import {tomGruppeModell} from "felles-ng2/modell/tommeModeller";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";

const debug = initDebugger('GruppeRedigerKomponent');

export type SkjemaType = GruppeRedigerKomponent['skjema'];
export type SkjemaVerdier = SkjemaType['value'];

/**
 * Skjema for redigering av gruppe.
 */
@Component({
    selector: 'gruppe-rediger',
    templateUrl: 'gruppe-rediger.html',
    styleUrl: 'gruppe-rediger.scss',
    host: { 'data-testid': 'gruppe-rediger' },
    imports: [
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInput,
        MatButton,
        MatSlideToggleModule,
        TranslateModule,
        StandardValideringsfeilPipe,
        NavigasjonforhindretFlashDirektiv,
        InlineSkjemaKomponent,
        InlineSkjemaTittelKomponent,
        InlineSkjemaHandlingerKomponent,
        VisSpinnerDirektiv,
        InlineMeldingKomponent,
        LokaleVelgerKomponent,
        TidssoneVelgerKomponent,
        KreverTilgangsrolleBrukerDirektiv,
        GruppeLogoOgFargeSkjemaKomponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GruppeRedigerKomponent {

    vedAvbryt = output<void>();
    vedLagret = output<void>();

    gruppeId = input.required<GruppeId>();
    gruppe: Signal<EnkelGruppeModell>;

    lagrer = asynkArbeidPågårSignal()

    skjema = new FormGroup({
        tittel: new FormControl('', {nonNullable: true, validators: FeltValidatorer.litenIkkeTomStreng}),
        moetenavn: new FormControl('', {nonNullable: true, validators: FeltValidatorer.litenIkkeTomStreng}),
        organisasjonsnavn: new FormControl('', {nonNullable: true, validators: FeltValidatorer.litenIkkeTomStreng}),
        spraak: new FormControl(Språk.norsk_bokmål, {nonNullable: true, validators: Validators.required}),
        tidssone: new FormControl('', {nonNullable: true, validators: Validators.required}),
        erMal: new FormControl(false, {nonNullable: true}),
        logoOgFarge: GruppeLogoOgFargeSkjemaKomponent.opprettSkjema(),
        gruppemal: new FormGroup({
            navn: new FormControl('', {nonNullable: true, validators: FeltValidatorer.litenIkkeTomStreng}),
            beskrivelse: new FormControl('', {nonNullable: true, validators: FeltValidatorer.litenIkkeTomStreng}),
        })
    });

    constructor(private avhengigheter: GruppeRedigerKomponentAvhengigheter, private konfigurasjon: Appkonfigurasjon) {
        //hent data, stopp å hente når gruppe hentet fra server er levert
        this.gruppe = toSignal(toObservable(this.gruppeId)
            .pipe(switchMap(id => this.avhengigheter.hent(id)),
              terminerEtterFørsteRequestRespons(),
              logMottatt(debug),
              mapTilData(),
            ),
          {initialValue: tomGruppeModell()});

        //sørg for å aktivere gruppe-mal-skjema når gruppe markere som mal
        this.skjema.controls.erMal.valueChanges
          .pipe(takeUntilDestroyed())
          .subscribe(erMal => this.aktiverGruppemalSkjema(erMal));

        //oppdater skjema når gruppe endres
        effect(() => this.populerFraGruppe(this.gruppe()));
    }

    async lagre() {
        let spesifikasjon = await this.somSpesifikasjon();
        await this.lagrer.utfør(() =>
          this.avhengigheter.oppdater(this.gruppe().id, spesifikasjon));
        this.vedLagret.emit();
    }

    avbryt() {
        this.vedAvbryt.emit();
    }

    //private hjelpemetoder

    private populerFraGruppe(gruppe: EnkelGruppeModell) {
        let erMal = gruppe.gruppetype == Gruppetype.mal;
        this.skjema.patchValue({
            tittel: gruppe.tittel,
            moetenavn: gruppe.moetenavn,
            organisasjonsnavn: gruppe.organisasjonsnavn,
            spraak: gruppe.oppsett.spraak,
            tidssone: gruppe.oppsett.tidssone,
            erMal: erMal,
            logoOgFarge: {farge: gruppe.farge},
            gruppemal: {
                navn: gruppe.gruppemal?.navn,
                beskrivelse: gruppe.gruppemal?.beskrivelse
            }
        });
        this.aktiverGruppemalSkjema(erMal);
    }

    private aktiverGruppemalSkjema(erMal: boolean) {
        aktiverFormKontroller(erMal, this.skjema.controls.gruppemal, {emitEvent: false})
    }

    private async somSpesifikasjon(): Promise<OppdaterGruppeSpesifikasjon> {
        let verdier = this.skjema.value;
        let spesifikasjon: OppdaterGruppeSpesifikasjon = {
            tittel: verdier.tittel!,
            moetenavn: verdier.moetenavn!,
            organisasjonsnavn: verdier.organisasjonsnavn!,
            gruppetype: Gruppetype.ordinær,
            spraak: verdier.spraak!,
            tidssone: verdier.tidssone!,
            logoBase64: verdier.logoOgFarge!.logo ?? null,
            farge: verdier.logoOgFarge!.farge!,
            gruppemal: null
        };
        if (verdier.erMal) {
            spesifikasjon.gruppetype = Gruppetype.mal;
            spesifikasjon.gruppemal = {
                navn: verdier.gruppemal!.navn!,
                beskrivelse: verdier.gruppemal!.beskrivelse!
            }
        }
        return spesifikasjon;
    }
}

@Injectable()
export class GruppeRedigerKomponentAvhengigheter {

    constructor(private gruppeDatatjeneste: GruppeDatatjeneste, private kontekstAdministrasjon: BrukerkontekstAdministrasjon) {
    }

    hent(gruppeId: GruppeId): Observable<DataOgMeta<GruppeModellDML>> {
        return this.gruppeDatatjeneste.hentMedMeta(gruppeId);
    }

    async oppdater(gruppeId: GruppeId, spesifikasjon: OppdaterGruppeSpesifikasjon): Promise<any> {
        const oppdatertGruppe = await this.gruppeDatatjeneste.oppdater(gruppeId, spesifikasjon);
        //må relaste kontekst da gruppe har blitt oppdatert
        this.kontekstAdministrasjon.relastKontekst();
        return oppdatertGruppe;
    }
}
